import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import moment from 'moment';
import { DeleteAppService } from '../saved-quotes/services/delete-app.service';
import { SaveQuoteModalTriggerService } from '../saved-quotes/services/save-quote-modal-trigger.service';
import { environment } from 'src/environments/environment';
import { MomentTimeZoneEnum } from 'src/app/shared/enums/moment-timezone.enum';
import { ENROLLMENT_FLOW } from '../../form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/enrollment-flow.enum';
import { BooleanInput } from '@ptl/global-components';
import { SaveQuoteModalArgs, SEP_FAMILY_HOUSEHOLD_CHANGE_EVENTS } from '../saved-quotes/interfaces/save-quote-data.interface';
import { firstValueFrom } from 'rxjs';
import { SaveQuoteModalComponent } from '../save-quote-modal/save-quote-modal.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SaveQuoteModalConfigurationData } from '../save-quote-modal/interfaces/save-quote-modal.interface';
import { IndividualMarketSaveQuoteUtilityService } from '../saved-quotes/services/individual-market-save-quote-utility.service';
import { SavedQuotesInputs, SaveQuoteCardDeleteQuoteEvent } from '../saved-quotes/interfaces/saved-quotes.input.interface';
import { EnrollPayloadSaveRequest } from '../../shared/interfaces/enrollment-submission.interface';
import { ApplicationRecord } from '../interfaces/application-record.interface';
import { SaveQuoteReturnUrlService } from '../saved-quotes/services/save-quote-return-url.service';
import { SaveQuoteEmailReferralModalComponent } from '../save-quote-email-referral-modal/save-quote-email-referral-modal.component';
import { ModalFieldConfigs, SaveQuoteEmailReferralModalArgs } from '../saved-quotes/interfaces/save-quote-email-referral.interface';
import { SaveQuoteEmailReferralService } from '../saved-quotes/services/save-quote-email-referral.service';
import { AbstractControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { EmailValidatorService } from '../../shared/services/email-validator.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { RetrieveApp } from 'src/app/shared/interfaces/application-retrieval/retrieveApp.interface';

interface EmailForm{
  customerEmailAddress: string;
}
@Component({
    selector: 'app-save-quote-card',
    templateUrl: './save-quote-card.component.html',
    styleUrls: ['./save-quote-card.component.scss'],
    standalone: false
})
export class SaveQuoteCardComponent implements OnInit {

  @Input() planNameLabel: string;
  @Input() planMonthlyPremiumLabel: string;
  @Input() planDeductibleLabel: string;
  @Input() creationDateLabel: string;
  @Input() deleteQuoteButtonLabel: string;
  @Input() continueApplicationButtonLabel: string;
  @BooleanInput() @Input() showExpiryNotice: boolean;

  @Input() oepEndDate: Date | string;
  @Input() lastDayJanuaryEffectiveDate: Date | string;
  
  @Input() quoteType: SavedQuotesInputs["TQuoteType"];

  @BooleanInput() @Input() disableEmailReferral: boolean; // Controls whether we see the email fields in the card
  @Input() disableReferralTooltip: string  = "";
  @Input() fieldsToDisplay: string;
  @BooleanInput() @Input() useEmailModal: boolean = true;
  @Input() emailFieldLabel: string;
  @Input() emailFieldPlaceholder: string;
  @Input() sendEmailButtonLabel: string;
  @Input() emailSuccessStatusMessage: string;

  //Non-Configurable Variables
  @Input() applicationRecord: ApplicationRecord;
  @Input() appStatus: string;
  @Input() consumerDocumentUploadReturnUrl: string = "/prompt/document-upload/begin/special-enrollment-online";
  @Input() additionalModalFields: string;

  @Output() onDeleteQuote: EventEmitter<{ id: string, name: string }> = new EventEmitter();

  quoteButtonPage: string; 

  displayPremium: string;
  displayDeductible: string;
  daysUntilExpiration: number;
  expirationDate: string;
  emailTriggerIsPending: boolean;
  continueQuoteIsPending: boolean;

  enableEmailForm: boolean;

  timeZone = MomentTimeZoneEnum[environment.configs.siteTimeZone];

  producerUrlPaths: string[];

  isSep: boolean = false;
  isOep: boolean = false;
  appType: string;
  enrollmentFlow: string;
  emailForm: UntypedFormGroup;
  sentEmail: boolean; 
  noNpnTooltip: string = "";
  additionalModalFieldsJSON: ModalFieldConfigs[];

  
  constructor(
    private fb: UntypedFormBuilder,
    private deleteQuoteService: DeleteAppService,
    private saveQuoteModalTriggerService: SaveQuoteModalTriggerService,
    private individualMarketSaveQuoteUtilityService: IndividualMarketSaveQuoteUtilityService,
    private saveQuoteReturnUrlService: SaveQuoteReturnUrlService,
    private matDialog: MatDialog,
    private saveQuoteEmailReferralService: SaveQuoteEmailReferralService,
    private matSnackBar: MatSnackBar
  ) {
    this.emailForm = this.fb.group({
      customerEmailAddress: this.fb.control('',[
        Validators.required,
        EmailValidatorService.createEmailValidator(),
        Validators.maxLength(75)
      ]),
    });
  }

  get formControls(): { customerEmailAddress: AbstractControl } {
    return this.emailForm.controls as unknown as { customerEmailAddress: AbstractControl };
  }

  get formValuesAsJson(): EmailForm {
    return this.emailForm.value as EmailForm
  }

  async ngOnInit() {

    this.saveQuoteReturnUrlService.getContent();
    this.enrollmentFlow = this.applicationRecord.enrollmentObjectJson?.enrollment.enrollmentFlow;
    this.enableEmailForm = !!(this.saveQuoteEmailReferralService.isEligibleToSendQuotes() && !this.disableEmailReferral)
    
    this.isSep = (() => {
      return (
        this.enrollmentFlow === ENROLLMENT_FLOW.SEP_ELECTRONIC ||
        this.enrollmentFlow === ENROLLMENT_FLOW.SEP_PAPER
      )
    })();

    this.isOep = (() => {
      return (
        this.enrollmentFlow === ENROLLMENT_FLOW.OEP
      )
    })();

    this.appType = this.isSep ? 'SEP' : 'OEP';

    this.displayDeductible = this.applicationRecord.productDeductible ? (this.applicationRecord.productDeductible.includes('$') ? this.applicationRecord.productDeductible : '$' + this.applicationRecord.productDeductible) : 'N/A';
    this.displayPremium = this.applicationRecord.productMonthlyPremium ? (this.applicationRecord.productMonthlyPremium.includes('$') ? this.applicationRecord.productMonthlyPremium : '$' + this.applicationRecord.productMonthlyPremium) : 'N/A';
    this.expirationDate = await this.calculateDaysUntilExpiration(this.applicationRecord);
    await this.deleteQuoteIfExpired();

    this.emailForm = this.fb.group({
      customerEmailAddress: this.fb.control('',[
        Validators.required,
        EmailValidatorService.createEmailValidator(),
        Validators.maxLength(75)
      ]),
    });
    
    if (!this.enableEmailForm) {
      this.formControls.customerEmailAddress.disable();
    }
  }

  async deleteQuoteIfExpired() {
    const expirationNoTz = moment(this.expirationDate).toISOString().split("T")[0]
    const expirationAsMoment = moment.tz(expirationNoTz, this.timeZone);
    if (expirationAsMoment.isSameOrBefore(moment().tz(this.timeZone))) {
      console.log(`Quote Deleted
        Application ID: ${this.applicationRecord.applicationTrackingId}
        Effective Date: ${this.applicationRecord.effectiveDate}
        Calculated Expiration Date: ${expirationAsMoment}`)
      await this.deleteQuote(true);
    }
  }

  async calculateDaysUntilExpiration(plan: ApplicationRecord): Promise<string> {

    /** For SEP Life events we have two categories, family changes & non-family changes.
     * Family change events like Birth, Adoption, etc. are the only life events that can have a date of life event eff date.
     * Any other life event will have an effective date of 1st of the month of the NEXT month from today's date. 
     * Because family change events can have an effective date in the past, we expire quotes 60 days from the DATE OF LIFE EVENT
     * All other events have an expiration date of the day of the effective date
     */

    const today = moment().tz(this.timeZone);
    const creationDate = moment(plan.creationDate).tz(this.timeZone);
    const oepEndDateNoTz = moment(this.oepEndDate).toISOString().split("T")[0]
    const oepEndDateInclusive = (moment.tz(oepEndDateNoTz, this.timeZone)).add(1, 'day');
    const janEffDateEndNoTz =  moment(this.lastDayJanuaryEffectiveDate).toISOString().split("T")[0]
    const lastDayJanuaryEffectiveDateInclusive = (moment.tz(janEffDateEndNoTz, this.timeZone)).add(1, 'day');

    if (this.isSep) {
      const sepLifeEvent = plan.enrollmentObjectJson.enrollment.sepInformation.lifeEvent;
      if (sepLifeEvent.date && sepLifeEvent.date !== "") {
        if(SEP_FAMILY_HOUSEHOLD_CHANGE_EVENTS.includes(sepLifeEvent.eventId)){
          //add only 59 days from the event instead of 60 because we count the day of the event 
          const sepExpirationNoTz = moment(sepLifeEvent.date).toISOString().split("T")[0];
          const sepExpirationDateAsMoment = moment.tz(sepExpirationNoTz, this.timeZone).add(59, 'days');
          this.daysUntilExpiration = sepExpirationDateAsMoment.diff(today, 'days');
          return sepExpirationDateAsMoment.format('MMM DD, YYYY');
        } else {
          const effectiveDateNoTz = moment(plan.effectiveDate).toISOString().split("T")[0];
          const effectiveDateAsMoment = moment.tz(effectiveDateNoTz, this.timeZone);
          return (effectiveDateAsMoment).format('MMM DD, YYYY');
        }
      }
    } else if (this.isOep && plan.combinedStatus === "IN PROGRESS"){
      if (creationDate.isBefore(lastDayJanuaryEffectiveDateInclusive)) {
        this.daysUntilExpiration = (lastDayJanuaryEffectiveDateInclusive.diff(today, 'days'));
        return lastDayJanuaryEffectiveDateInclusive.format('MMM DD, YYYY');
      } else if (creationDate.isBetween(lastDayJanuaryEffectiveDateInclusive, oepEndDateInclusive)) {
        this.daysUntilExpiration = (oepEndDateInclusive.diff(today, 'days'));
        return (oepEndDateInclusive).format('MMM DD, YYYY');
      }else{
        return moment().format('MMM DD, YYYY');
      }
    }
  }

  async continueQuote(plan: ApplicationRecord) {
    this.continueQuoteIsPending = true;

    const response = await this.individualMarketSaveQuoteUtilityService.retrieveApp(this.applicationRecord);
    if(response instanceof HttpErrorResponse){
      console.error(
        `No Application Found!
        applicationRecord: ${this.applicationRecord}`
      );
    }else{
      await this.individualMarketSaveQuoteUtilityService.loadSaveQuoteDataToState({producerInfo: {plan, savedData: (response as RetrieveApp)}})

      const saveQuoteEnrollmentObj: EnrollPayloadSaveRequest = (response as RetrieveApp).enrollementObject as EnrollPayloadSaveRequest;
      const returnUrl = this.saveQuoteReturnUrlService.getProducerReturnUrl(saveQuoteEnrollmentObj.saveQuoteData);

      if (returnUrl) {
        this.continueQuoteIsPending = false;
        window.location.href = returnUrl;
      }

      this.continueQuoteIsPending = false;
    }
  }


  async deleteQuote(skipExternalAlert: boolean = false) {

    const data: SaveQuoteCardDeleteQuoteEvent = {
      id: this.applicationRecord.applicationTrackingId,
      name: this.applicationRecord.statusDescription,
      showAlert: !skipExternalAlert
    };

    await this.deleteQuoteService.deleteApplication(data.id);
    
    this.onDeleteQuote.next(data);
    
  }

  async renameQuote(plan: ApplicationRecord) {

    const modalArgs: SaveQuoteModalArgs = {
      urls: null,
      enrollmentFlow: !!plan.enrollmentObjectJson?.enrollment.sepInformation ? ENROLLMENT_FLOW.SEP_ELECTRONIC : ENROLLMENT_FLOW.OEP
    };

    const saveQuoteModal: MatDialogRef<SaveQuoteModalComponent, SaveQuoteModalConfigurationData>
      = this.saveQuoteModalTriggerService.openSaveQuoteModal({
          modalArgs: modalArgs,
          resumptionLandingPage: this.quoteButtonPage,
          plan: plan
        });

    const modalResponseData = await firstValueFrom(saveQuoteModal.afterClosed());
    this.applicationRecord.statusDescription = modalResponseData.plan.statusDescription; // this will update the card with the new name, without reload
  }

  async triggerEmail(form: FormGroup){
    if(this.formValuesAsJson.customerEmailAddress) {
      this.emailTriggerIsPending = true;
      await this.saveQuoteEmailReferralService.sendEmail(form, this.applicationRecord, this.quoteType, this.consumerDocumentUploadReturnUrl);
      this.matSnackBar.open(this.emailSuccessStatusMessage, 'OK', {
        duration: 3000,
        panelClass: 'positive-panel-class'
      })
      this.emailTriggerIsPending = false;
    }
  }

  openEmailReferralModal(){
    this.additionalModalFieldsJSON = JSON.parse((this.additionalModalFields as string));
    const parsedAdditionalModalFields = this.additionalModalFieldsJSON.filter((field =>  field.fieldName != "name" && field.fieldName != "agentNotes"));
    const nameField = this.additionalModalFieldsJSON.find((field => field.fieldName === "name"));
    const agentNotesField = this.additionalModalFieldsJSON.find((field => field.fieldName === "agentNotes"));
    const modalArgs: SaveQuoteEmailReferralModalArgs = {
      applicationRecord: this.applicationRecord,
      quoteType: this.quoteType,
      consumerDocumentUploadReturnUrl: this.consumerDocumentUploadReturnUrl,
      defaultFieldData: {
        emailSuccessStatusMessage: this.emailSuccessStatusMessage,
        emailFieldLabel: this.emailFieldLabel, 
        emailFieldPlaceholder: this.emailFieldPlaceholder,
        nameFieldLabel: nameField.label,
        nameFieldPlaceholder: nameField.placeholder,
        agentNotesFieldLabel: agentNotesField.label,
        agentNotesFieldPlaceholder: agentNotesField.placeholder
      },
      additionalModalFields: parsedAdditionalModalFields
    }
    return this.matDialog.open(SaveQuoteEmailReferralModalComponent, {
      minWidth: "300px",
      width: "30vw",
      maxHeight: "90vh",
      disableClose: true,
      data: modalArgs
    })
  }

}
